import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CATEGORY_LABEL, INR } from "@/lib/constants";
import { Search, Ban, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, X, KeyRound, Copy, CheckCircle2, Pencil, UserX } from "lucide-react";
import { toast } from "sonner";
import MembersDrilldownDialog from "@/components/MembersDrilldownDialog";

const CATEGORY_OPTIONS = ["OPEN_GYM", "GROUP_TRAINING", "PT", "SEMI_PT", "RECOVERY"];
const STATUS_OPTIONS = ["ACTIVE", "EXPIRED", "CANCELLED", "TRIAL"];
const ATT_OPTIONS = [
  { key: "all", label: "All" },
  { key: "green", label: "Green · 80%+" },
  { key: "yellow", label: "Yellow · 60–79%" },
  { key: "red", label: "Red · below 60%" },
];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function BucketTile({ b, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left w-full group focus:outline-none focus:ring-2 focus:ring-volt/50 rounded-sm"
    >
      <Panel className="hover:border-volt/40 transition-colors cursor-pointer">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: b.color }}>{b.label}</div>
            <div className="font-display text-5xl font-black mt-1" style={{ color: b.color }} data-testid={b.countTestid}>
              {count}
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-1 group-hover:text-volt transition-colors">
              Click to view →
            </div>
          </div>
          <div className="w-12 h-12 rounded-sm grid place-items-center" style={{ background: `${b.color}22`, color: b.color }}>
            <span className="font-display font-bold text-lg">{b.d}d</span>
          </div>
        </div>
      </Panel>
    </button>
  );
}

export default function MembersPage() {
  const { user, activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  // Deep-link support: /members?q=<phone-or-name> — used by the Sales
  // Pipeline "Renewal Due" card click so the sales rep lands on the right
  // member row instantly.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const initial = searchParams.get("q");
    if (initial) {
      setQ(initial);
      // Clear the query param so refreshing doesn't re-apply it
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      setSearchParams(next, { replace: true });
    }
  }, []); // Only run on mount — the URL is consumed once
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [attFilter, setAttFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [drilldown, setDrilldown] = useState(null); // {title, subtitle, members, metric, showNudge}
  const [pwReset, setPwReset] = useState(null); // {email, full_name, role, new_password}
  const [editMember, setEditMember] = useState(null); // member being edited
  const params = activeBranchId ? { branch_id: activeBranchId } : {};

  const resetPassword = useMutation({
    mutationFn: async (m) => (await apiClient.post(`/users/${m.user_id}/reset-password`)).data,
    onSuccess: (d) => setPwReset(d),
    onError: (e) => toast.error(e?.response?.data?.detail || "Password reset failed"),
  });

  const requestEdit = useMutation({
    mutationFn: async ({ member, changes }) => (await apiClient.post("/approvals", {
      action_type: "MEMBER_EDIT",
      target_id: member.user_id,
      payload: { member_name: member.name, ...changes },
      branch_id: member.branch_id,
    })).data,
    onSuccess: (d) => {
      if (d.auto_approved) {
        toast.success("Member updated");
        qc.invalidateQueries({ queryKey: ["members"] });
      } else {
        toast.success("Edit requested — Super Admin will review");
      }
      setEditMember(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // Direct preference update — bypasses approval queue for soft coaching prefs.
  const updatePrefs = useMutation({
    mutationFn: async ({ user_id, prefs }) => (await apiClient.patch(`/members/${user_id}/preferences`, prefs)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["members"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Preferences update failed"),
  });

  const requestDeactivate = useMutation({
    mutationFn: async (m) => (await apiClient.post("/approvals", {
      action_type: "MEMBER_DEACTIVATE",
      target_id: m.user_id,
      payload: { member_name: m.name },
      branch_id: m.branch_id,
    })).data,
    onSuccess: (d) => {
      if (d.auto_approved) {
        toast.success("Member deactivated");
        qc.invalidateQueries({ queryKey: ["members"] });
      } else {
        toast.success("Deactivation requested — Super Admin will review");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const requestCancel = useMutation({
    mutationFn: async (m) => (await apiClient.post("/approvals", {
      action_type: "MEMBERSHIP_CANCEL",
      target_id: m.active_membership?.id,
      payload: { member_name: m.name, plan: m.active_membership?.plan_name },
      branch_id: m.branch_id,
    })).data,
    onSuccess: (d) => {
      if (d.auto_approved) {
        toast.success("Membership cancelled (Super Admin auto-approved)");
        qc.invalidateQueries({ queryKey: ["members"] });
      } else {
        toast.success("Cancellation requested — Super Admin will review");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members", activeBranchId],
    queryFn: async () => (await apiClient.get("/members", { params })).data,
  });
  const { data: expiringData } = useQuery({
    queryKey: ["expiring", activeBranchId],
    queryFn: async () => (await apiClient.get("/memberships/expiring", { params })).data,
  });
  const { data: newThisMonth = [] } = useQuery({
    queryKey: ["new-this-month", activeBranchId],
    queryFn: async () => (await apiClient.get("/members/new-this-month", { params })).data,
  });

  const expiring = expiringData?.expiring || {};
  const expired = expiringData?.expired_not_renewed || {};

  const bucketColors = { "7": "#FF3B30", "15": "#FFEA00", "30": "#7DD3FC" };
  const openDrill = (kind, days) => {
    const isExpired = kind === "expired";
    const list = (isExpired ? expired : expiring)?.[days] || [];
    setDrilldown({
      title: isExpired ? `Expired · Last ${days} days` : `Renewing in ${days} days`,
      subtitle: `${list.length} member${list.length === 1 ? "" : "s"} — click WhatsApp to send a renewal offer`,
      members: list,
      metric: isExpired ? "days_expired" : "days_left",
      showNudge: true,
    });
  };

  // ---- Full roster: filter → sort → paginate ----
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return members.filter((m) => {
      if (s && !(m.name || "").toLowerCase().includes(s) && !(m.email || "").toLowerCase().includes(s) && !(m.phone || "").toLowerCase().includes(s)) {
        return false;
      }
      const cat = m.active_membership?.category;
      if (categoryFilter !== "all" && cat !== categoryFilter) return false;
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      const att = m.attendance_pct || 0;
      if (attFilter === "green" && att < 80) return false;
      if (attFilter === "yellow" && (att < 60 || att >= 80)) return false;
      if (attFilter === "red" && att >= 60) return false;
      return true;
    });
  }, [members, q, categoryFilter, statusFilter, attFilter]);

  const sorted = useMemo(() => {
    const asc = sortDir === "asc" ? 1 : -1;
    const getVal = (m) => {
      switch (sortBy) {
        case "name": return (m.name || "").toLowerCase();
        case "plan": return m.active_membership?.plan_name || "";
        case "status": return m.status || "";
        case "attendance": return m.attendance_pct || 0;
        case "last_visit": return m.last_visit || "";
        case "loyalty": return m.loyalty_points || 0;
        default: return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      if (av === bv) return 0;
      if (typeof av === "number") return (av - bv) * asc;
      return String(av).localeCompare(String(bv)) * asc;
    });
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pageRows = sorted.slice(pageStart, pageEnd);

  React.useEffect(() => { setPage(1); }, [q, categoryFilter, statusFilter, attFilter, pageSize]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir(col === "attendance" || col === "loyalty" ? "desc" : "asc"); }
  };
  const SortHead = ({ col, children, align = "left" }) => (
    <th className={`text-${align} py-2 px-2 font-semibold cursor-pointer select-none hover:text-volt transition-colors`} onClick={() => toggleSort(col)}>
      <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {children}
        {sortBy === col && (sortDir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </div>
    </th>
  );

  const anyFilterActive = q || categoryFilter !== "all" || statusFilter !== "all" || attFilter !== "all";

  const greenCount = members.filter((m) => (m.attendance_pct || 0) >= 80).length;
  const yellowCount = members.filter((m) => (m.attendance_pct || 0) >= 60 && (m.attendance_pct || 0) < 80).length;
  const redCount = members.filter((m) => (m.attendance_pct || 0) < 60).length;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="members-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Membership</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Members</h1>
          <div className="text-zinc-500 text-sm mt-1">
            {members.length} total · Green {greenCount} · Yellow {yellowCount} · Red {redCount}
          </div>
        </div>
      </div>

      {/* Dropped Out (archived renewals) — Super Admin can archive stale entries */}
      <DroppedOutSection role={user?.role} />

      {/* Renewal Pulse — clickable */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2">Renewal Pulse</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { d: "7", color: "#FF3B30", label: "Expiring in 7 days", countTestid: "expiring-count-7" },
            { d: "15", color: "#FFEA00", label: "Expiring in 15 days", countTestid: "expiring-count-15" },
            { d: "30", color: "#7DD3FC", label: "Expiring in 30 days", countTestid: "expiring-count-30" },
          ].map((b) => (
            <BucketTile key={`exp-${b.d}`} b={b} count={expiring?.[b.d]?.length ?? 0}
              testid={`expiring-tile-${b.d}`} onClick={() => openDrill("expiring", b.d)} />
          ))}
        </div>
      </div>

      {/* Expired — clickable */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2">Membership Expired</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { d: "7", color: "#FF3B30", label: "Expired in last 7 days", countTestid: "expired-count-7" },
            { d: "15", color: "#FFEA00", label: "Expired in last 15 days", countTestid: "expired-count-15" },
            { d: "30", color: "#7DD3FC", label: "Expired in last 30 days", countTestid: "expired-count-30" },
          ].map((b) => (
            <BucketTile key={`expd-${b.d}`} b={b} count={expired?.[b.d]?.length ?? 0}
              testid={`expired-tile-${b.d}`} onClick={() => openDrill("expired", b.d)} />
          ))}
        </div>
      </div>

      {/* New this month */}
      <Panel>
        <PanelHeader
          subtitle={`${new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })} · ${newThisMonth.length} new`}
          title="Joined This Month"
        />
        {newThisMonth.length === 0 ? (
          <div className="text-zinc-600 text-sm py-6 text-center">No new members joined this month yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="new-this-month-grid">
            {newThisMonth.slice(0, 9).map((m) => (
              <div key={m.id} data-testid={`new-member-${m.member_id}`}
                className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 hover:border-volt transition-colors">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{m.member_name || "—"}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{m.member_email}</div>
                  </div>
                  <Badge variant="outline" className="border-volt/50 text-volt uppercase text-[10px] tracking-[0.16em] shrink-0">NEW</Badge>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{CATEGORY_LABEL[m.category] || m.category}</div>
                    <div className="text-xs text-zinc-300 truncate max-w-[180px]">{m.plan_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Paid</div>
                    <div className="font-display text-lg font-bold text-volt">{INR(m.price)}</div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  Joined {new Date(m.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Full Roster with filters + sort + pagination */}
      <Panel>
        <PanelHeader
          title="Full Roster"
          subtitle={`${sorted.length} of ${members.length} members · sort headers · use filters below`}
        />

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="member-search" placeholder="Search name / email / phone…" value={q} onChange={(e) => setQ(e.target.value)}
              className="bg-zinc-950 border-zinc-800 pl-9 rounded-sm" />
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="filter-category" className="w-40 bg-zinc-950 border-zinc-800 rounded-sm text-xs uppercase tracking-wider">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-36 bg-zinc-950 border-zinc-800 rounded-sm text-xs uppercase tracking-wider">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={attFilter} onValueChange={setAttFilter}>
            <SelectTrigger data-testid="filter-attendance" className="w-44 bg-zinc-950 border-zinc-800 rounded-sm text-xs uppercase tracking-wider">
              <SelectValue placeholder="Attendance" />
            </SelectTrigger>
            <SelectContent>
              {ATT_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          {anyFilterActive && (
            <Button variant="outline" size="sm" data-testid="clear-filters"
              onClick={() => { setQ(""); setCategoryFilter("all"); setStatusFilter("all"); setAttFilter("all"); }}
              className="bg-zinc-950 border-zinc-800 text-zinc-300 rounded-sm text-[10px] uppercase tracking-[0.16em]">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Filter size={12} className="text-zinc-500" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Rows</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="page-size" className="w-20 h-8 bg-zinc-950 border-zinc-800 rounded-sm text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="members-table">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              <tr className="border-b border-zinc-900">
                <SortHead col="name">Name</SortHead>
                <SortHead col="plan">Plan</SortHead>
                <SortHead col="status">Status</SortHead>
                <SortHead col="attendance">Attendance</SortHead>
                <SortHead col="last_visit">Last Visit</SortHead>
                <SortHead col="loyalty" align="right">Loyalty</SortHead>
                <th className="text-right py-2 px-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((m) => {
                const att = m.attendance_pct || 0;
                const color = att >= 80 ? "#00E676" : att >= 60 ? "#FFEA00" : "#FF3B30";
                const cat = m.active_membership?.category;
                return (
                  <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-zinc-900 hover:bg-zinc-950">
                    <td className="py-3 px-2">
                      <div className="font-semibold">{m.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[220px]">{m.email}</div>
                    </td>
                    <td className="py-3 px-2">
                      {cat ? (
                        <Badge variant="outline" className="border-zinc-800 text-zinc-300 uppercase text-[10px] tracking-[0.16em]">
                          {CATEGORY_LABEL[cat] || cat}
                        </Badge>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-xs px-2 py-0.5 rounded-sm" style={{ background: `${color}22`, color }}>{m.status}</span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                          <div className="h-full" style={{ width: `${Math.min(att, 100)}%`, background: color }} />
                        </div>
                        <span className="font-display font-bold" style={{ color }}>{Math.round(att)}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-zinc-400 whitespace-nowrap">
                      {m.last_visit ? new Date(m.last_visit).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                    </td>
                    <td className="py-3 px-2 text-right text-volt font-display font-bold">{m.loyalty_points || 0}</td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") && m.user_id && (
                          <Button size="sm" variant="outline" onClick={() => setEditMember(m)}
                            data-testid={`edit-member-${m.user_id}`}
                            className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-volt hover:text-volt text-[10px] uppercase tracking-[0.16em]">
                            <Pencil size={10} className="mr-1" /> Edit
                          </Button>
                        )}
                        {user?.role === "SUPER_ADMIN" && m.user_id && (
                          <Button size="sm" variant="outline" onClick={() => resetPassword.mutate(m)}
                            disabled={resetPassword.isPending}
                            data-testid={`reset-pw-${m.user_id}`}
                            className="bg-zinc-900 border-zinc-800 text-volt hover:border-volt text-[10px] uppercase tracking-[0.16em]">
                            <KeyRound size={10} className="mr-1" /> Reset PW
                          </Button>
                        )}
                        {m.active_membership && (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") && (
                          <Button size="sm" variant="outline" onClick={() => requestCancel.mutate(m)} data-testid={`request-cancel-${m.user_id}`}
                            className="bg-zinc-900 border-zinc-800 text-red-400 hover:border-red-500 text-[10px] uppercase tracking-[0.16em]">
                            <Ban size={10} className="mr-1" /> Cancel
                          </Button>
                        )}
                        {(user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") && m.user_id && (
                          <Button size="sm" variant="outline"
                            onClick={() => { if (window.confirm(`Deactivate member "${m.name}"? This will also cancel any active memberships.`)) requestDeactivate.mutate(m); }}
                            data-testid={`deactivate-member-${m.user_id}`}
                            className="bg-zinc-900 border-red-900/60 text-red-400 hover:border-red-500 text-[10px] uppercase tracking-[0.16em]">
                            <UserX size={10} className="mr-1" /> Deactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-zinc-600">
                  No members match your filters
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {sorted.length > pageSize && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-900">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              Showing {pageStart + 1}–{Math.min(pageEnd, sorted.length)} of {sorted.length}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
                className="bg-zinc-950 border-zinc-800 rounded-sm h-8"><ChevronLeft size={14} /></Button>
              <div className="text-xs font-mono text-zinc-300" data-testid="page-indicator">{page} / {totalPages}</div>
              <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"
                className="bg-zinc-950 border-zinc-800 rounded-sm h-8"><ChevronRight size={14} /></Button>
            </div>
          </div>
        )}
      </Panel>

      <MembersDrilldownDialog
        open={!!drilldown}
        onOpenChange={(o) => { if (!o) setDrilldown(null); }}
        {...(drilldown || {})}
      />

      <PasswordResetDialog data={pwReset} onClose={() => setPwReset(null)} />

      <EditMemberDialog
        member={editMember}
        role={user?.role}
        pending={requestEdit.isPending || updatePrefs.isPending}
        onSubmit={(changes) => requestEdit.mutate({ member: editMember, changes })}
        onSubmitPrefs={(prefs) => updatePrefs.mutateAsync({ user_id: editMember.user_id, prefs })}
        onClose={() => setEditMember(null)}
      />
    </div>
  );
}

const CORE_PROGRAMS_MEMBERS = [
  { code: "STRENGTH", label: "Strength" },
  { code: "CONDITIONING", label: "Conditioning" },
  { code: "MMA", label: "MMA" },
  { code: "HYROX", label: "Hyrox" },
  { code: "OPEN_GYM", label: "Open Gym" },
];

function fmt12hMembers(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

function EditMemberDialog({ member, role, pending, onSubmit, onSubmitPrefs, onClose }) {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    preferred_program: "", preferred_timeslot: "",
  });
  const [slots, setSlots] = useState([]);
  React.useEffect(() => {
    if (member) setForm({
      full_name: member.name || "",
      phone: member.phone || "",
      email: member.email || "",
      preferred_program: member.preferred_program || "",
      preferred_timeslot: member.preferred_timeslot || "",
    });
  }, [member]);

  // Live timeslot lookup based on selected program + member's branch.
  React.useEffect(() => {
    if (!member?.branch_id || !form.preferred_program) { setSlots([]); return; }
    const params = new URLSearchParams({ branch_id: member.branch_id, session_type: form.preferred_program });
    apiClient.get(`/public/time-slots?${params.toString()}`)
      .then((r) => setSlots(r.data || [])).catch(() => setSlots([]));
  }, [member?.branch_id, form.preferred_program]);

  const timeOptions = React.useMemo(() => {
    if (!slots.length) return [];
    if (form.preferred_program === "OPEN_GYM") {
      const s = slots[0];
      const range = s?.start && s?.end ? `${fmt12hMembers(s.start)} – ${fmt12hMembers(s.end)}` : "";
      return [{ value: s?.label || "OPEN_GYM_FLEX", label: `Anytime (walk-in)${range ? ` · ${range}` : ""}` }];
    }
    const uniq = Array.from(new Set(slots.map((s) => s.start).filter(Boolean))).sort();
    return uniq.map((t) => ({ value: t, label: fmt12hMembers(t) }));
  }, [slots, form.preferred_program]);

  const isSuper = role === "SUPER_ADMIN";
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    // Split changes:
    //  • identity fields (name/phone/email) → approval queue (Branch Admins request; Super Admin auto-approves)
    //  • preference fields (program/timeslot) → direct PATCH, no approval
    const idChanges = {};
    if (form.full_name !== (member?.name || "")) idChanges.full_name = form.full_name;
    if (form.phone !== (member?.phone || "")) idChanges.phone = form.phone;
    if (form.email !== (member?.email || "")) idChanges.email = form.email;

    const prefChanges = {};
    if (form.preferred_program !== (member?.preferred_program || "")) prefChanges.preferred_program = form.preferred_program || null;
    if (form.preferred_timeslot !== (member?.preferred_timeslot || "")) prefChanges.preferred_timeslot = form.preferred_timeslot || null;

    if (!Object.keys(idChanges).length && !Object.keys(prefChanges).length) {
      toast.info("No changes to submit"); return;
    }
    // Fire preference update first (fast, direct)
    if (Object.keys(prefChanges).length) {
      try { await onSubmitPrefs(prefChanges); toast.success("Program & timing updated"); }
      catch { return; /* toast already handled */ }
    }
    // Then identity changes via approval queue (if any)
    if (Object.keys(idChanges).length) {
      onSubmit(idChanges);
    } else {
      onClose();
    }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0b0b0b] border-zinc-800" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-zinc-100 flex items-center gap-2">
            <Pencil size={16} /> Edit Member
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm mt-2">
            {isSuper ? (
              <>Changes apply immediately.</>
            ) : (
              <>Your request will be sent to the Super Admin for approval.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Full Name</label>
            <Input value={form.full_name} onChange={(e) => upd("full_name", e.target.value)}
              data-testid="edit-member-name"
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Phone</label>
            <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)}
              data-testid="edit-member-phone"
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email</label>
            <Input value={form.email} onChange={(e) => upd("email", e.target.value)}
              data-testid="edit-member-email"
              className="bg-zinc-950 border-zinc-800 mt-1" />
          </div>
          <div className="border-t border-zinc-900 pt-3 mt-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-1">Offering &amp; Class Timing</div>
            <div className="text-[10px] text-zinc-600 mb-3">Program &amp; timing are soft preferences — updated instantly, no Super Admin approval needed.</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Offering (Program)</label>
                <Select value={form.preferred_program}
                  onValueChange={(v) => setForm((f) => ({ ...f, preferred_program: v, preferred_timeslot: "" }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-member-program">
                    <SelectValue placeholder="— select a program —" />
                  </SelectTrigger>
                  <SelectContent>
                    {CORE_PROGRAMS_MEMBERS.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Class Timing</label>
                <Select value={form.preferred_timeslot}
                  onValueChange={(v) => setForm((f) => ({ ...f, preferred_timeslot: v }))}
                  disabled={!form.preferred_program}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="edit-member-timeslot">
                    <SelectValue placeholder={form.preferred_program ? (timeOptions.length ? "— pick a time —" : "No times available") : "Pick a program first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.length === 0 ? (
                      <SelectItem value="__none__" disabled>No live schedule matches</SelectItem>
                    ) : timeOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-member-cancel"
            className="bg-zinc-900 border-zinc-800">Cancel</Button>
          <Button
            onClick={submit}
            disabled={pending}
            data-testid="edit-member-submit"
            className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            {pending ? "Submitting…" : (isSuper ? "Save Changes" : "Request Change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordResetDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(data?.new_password || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — long-press to copy manually"); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="bg-[#0b0b0b] border-volt/50" data-testid="pw-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-volt flex items-center gap-2">
            <KeyRound size={18} /> Password reset
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm mt-2">
            A new password has been set for <span className="text-zinc-200 font-semibold">{data?.full_name}</span>
            {" "}(<span className="font-mono-cmd">{data?.email}</span>).
            This is the only time it will be shown — copy it and share with the user securely.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 flex items-center justify-between gap-3 mt-3">
          <div className="font-mono-cmd text-2xl font-bold text-volt tracking-wider select-all" data-testid="pw-reset-password">
            {data?.new_password}
          </div>
          <Button
            onClick={doCopy}
            data-testid="pw-reset-copy"
            className="bg-zinc-900 border border-zinc-800 hover:border-volt text-volt uppercase tracking-wider rounded-sm">
            {copied ? <><CheckCircle2 size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
          </Button>
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">
          Ask the user to change their password after first login.
        </div>

        <DialogFooter className="mt-2">
          <Button
            onClick={onClose}
            data-testid="pw-reset-close"
            className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DroppedOutSection({ role }) {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["dropped-out-members"],
    queryFn: async () => (await apiClient.get("/members/dropped-out")).data,
  });
  const archive = useMutation({
    mutationFn: async () => (await apiClient.post("/renewals/archive-dropouts")).data,
    onSuccess: (r) => {
      toast.success(r.message || "Archived");
      qc.invalidateQueries({ queryKey: ["dropped-out-members"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (isLoading) return null;
  return (
    <div data-testid="dropped-out-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
          Dropped Out ({data.length})
        </div>
        {role === "SUPER_ADMIN" && (
          <Button
            variant="outline" size="sm"
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
            data-testid="archive-dropouts-btn"
            className="bg-zinc-950 border-zinc-800 hover:border-amber-400 text-zinc-300 hover:text-amber-300 uppercase tracking-[0.14em] text-[10px]"
          >
            {archive.isPending ? "Archiving…" : "Archive 30d+ Dropouts"}
          </Button>
        )}
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-zinc-600 bg-[#0a0a0a] border border-zinc-900 rounded-sm p-3">
          No archived Dropped Out members yet. Renewals that a rep drops (or that auto-close because the member renewed elsewhere) land here after 30 days.
        </div>
      ) : (
        <div className="bg-[#0a0a0a] border border-zinc-900 rounded-sm divide-y divide-zinc-900">
          {data.map((m) => (
            <div key={m.lead_id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`dropped-row-${m.user_id}`}>
              <div>
                <div className="text-sm font-semibold">{m.name}</div>
                <div className="text-[11px] text-zinc-500">{m.phone} · Last plan: {m.last_plan || "—"} · Expired {m.expired_on || "?"}</div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300/80">
                Dropped {m.dropped_out_at ? new Date(m.dropped_out_at).toLocaleDateString() : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
